import React, { useState, useCallback, useEffect } from 'react';
import BoxWrapper from '../boxwra';
import ConfirmationDialog from '../../../Sales/TDSReconciliationSale/ConfirmationDailog';
import { Snackbar, Alert } from '@mui/material';

// Helper function to safely parse number values
const safeParseFloat = (value) => {
  if (typeof value === 'number') return value;
  if (!value || value === '' || value === '-') return 0;
  const str = String(value);
  if (str.endsWith('.')) return 0;
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

function Six({ handleBack, updateTableData, data }) {
  const [tableData, setTableData] = useState(data || []);
  const [tableName] = useState("6.Details of ITC availed during the financial year");
  const [saveDailog, setSaveDailogOpen] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [confirmBack, setConfirmBack] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Dynamically find row indices
  const [rowIndices, setRowIndices] = useState({});

  useEffect(() => {
    const indices = {};
    data.forEach((item, index) => {
      const name = item.name.trim();
      
      // Normalize the name for matching (handle both "Sub-total" and "Sub total")
      const normalizedName = name.replace(/Sub-total/i, 'Sub total');
      
      if (normalizedName.includes('(A2) Net ITC of the financial year (A-A1)')) {
        indices.A2 = index;
      } else if (normalizedName.includes('(I) Sub total (B to H above)')) {
        indices.I = index;
      } else if (normalizedName.includes('(J) Difference (I - A2')) {
        indices.J = index;
      } else if (normalizedName.includes('(N) Sub total (K to M above)')) {
        indices.N = index;
      } else if (normalizedName.includes('(O) Total ITC availed (I + N)')) {
        indices.O = index;
      } else if (name.startsWith('(A) Total amount')) {
        indices.A = index;
      } else if (name.startsWith('(A1) ITC of any preceding')) {
        indices.A1 = index;
      } else if (name.startsWith('(B) Inward supplies') && !indices.B_start) {
        indices.B_start = index;
      } else if (name.startsWith('(H) Amount of ITC reclaimed')) {
        indices.H = index;
      } else if (name.startsWith('(K) Transition Credit through TRAN-1')) {
        indices.K = index;
      } else if (name.startsWith('(L) Transition Credit through TRAN-2')) {
        indices.L = index;
      } else if (name.startsWith('(M) ITC availed through ITC-01')) {
        indices.M = index;
      }
    });
    setRowIndices(indices);
  }, [data]);

  // --- CORE CALCULATION LOGIC ---
  const getSubTotalRowValue = useCallback((currentData, rowName, key_name) => {
    let value = 0;
    const normalizedName = rowName.trim().replace(/Sub-total/i, 'Sub total');

    if (normalizedName.includes('(A2) Net ITC of the financial year (A-A1)')) {
      const A_val = safeParseFloat(currentData[rowIndices.A]?.[key_name]); 
      const A1_val = safeParseFloat(currentData[rowIndices.A1]?.[key_name]);
      return (A_val - A1_val).toFixed(2);
    }

    if (normalizedName.includes('(I) Sub total (B to H above)')) {
      // Sum from B_start to H
      for (let i = rowIndices.B_start; i <= rowIndices.H; i++) {
        value += safeParseFloat(currentData[i]?.[key_name]);
      }
      return value.toFixed(2);
    }

    if (normalizedName.includes('(J) Difference (I - A2 above)') ){
      // Calculate I value
      let I_val = 0;
      for (let i = rowIndices.B_start; i <= rowIndices.H; i++) {
        I_val += safeParseFloat(currentData[i]?.[key_name]);
      }
      const A2_val = safeParseFloat(currentData[rowIndices.A2]?.[key_name]);
      return (I_val - A2_val).toFixed(2);
    }

    if (normalizedName.includes('(N) Sub total (K to M above)')) {
      // K, L, M with special handling
      for (let i = rowIndices.K; i <= rowIndices.M; i++) {
        // Skip IGST and CESS for K and L
        if ((i === rowIndices.K || i === rowIndices.L) && (key_name === 'igst' || key_name === 'cess')) {
          continue;
        }
        value += safeParseFloat(currentData[i]?.[key_name]);
      }
      return value.toFixed(2);
    }

    if (normalizedName.includes('(O) Total ITC availed (I + N)')) {
      let i_total = 0;
      let n_total = 0;
      
      // Calculate I_total (B to H)
      for (let i = rowIndices.B_start; i <= rowIndices.H; i++) {
        i_total += safeParseFloat(currentData[i]?.[key_name]);
      }
      
      // Calculate N_total (K to M) with special handling
      for (let i = rowIndices.K; i <= rowIndices.M; i++) {
        if ((i === rowIndices.K || i === rowIndices.L) && (key_name === 'igst' || key_name === 'cess')) {
          continue;
        }
        n_total += safeParseFloat(currentData[i]?.[key_name]);
      }
      return (i_total + n_total).toFixed(2);
    }

    return '0.00';
  }, [rowIndices]);

  // Check if a row is calculated
  const isCalculatedRow = useCallback((rowName) => {
    const normalized = rowName.trim().replace(/Sub-total/i, 'Sub total');
    return normalized.includes('(A2) Net ITC') ||
           normalized.includes('(I) Sub total (B to H') ||
           normalized.includes('(J) Difference') ||
           normalized.includes('(N) Sub total (K to M') ||
           normalized.includes('(O) Total ITC availed');
  }, []);

  // --- AUTO-CALCULATION FUNCTION ---
  const autoCalculate = useCallback((prevData) => {
    if (!rowIndices.A2) return prevData; // Wait for indices to be set
    
    let newData = [...prevData];
    let changed = false;

    // Calculate in order: A2 -> I -> J -> N -> O
    const calculationOrder = [
      rowIndices.A2,
      rowIndices.I,
      rowIndices.J,
      rowIndices.N,
      rowIndices.O
    ].filter(idx => idx !== undefined);

    for (const index of calculationOrder) {
      const item = newData[index];
      if (!item) continue;

      const newItem = { ...item };
      let itemChanged = false;

      ['igst', 'cgst', 'sgst', 'cess'].forEach((field) => {
        const newValue = getSubTotalRowValue(newData, item.name, field);
        const currentValue = String(safeParseFloat(newItem[field]));
        const calculatedValue = String(safeParseFloat(newValue));
        
        if (currentValue !== calculatedValue) {
          newItem[field] = newValue;
          itemChanged = true;
        }
      });

      if (itemChanged) {
        newData[index] = newItem;
        changed = true;
      }
    }

    return changed ? newData : prevData;
  }, [getSubTotalRowValue, rowIndices]);

  // --- SUBMIT AND NAVIGATION HANDLERS ---
  const submitHandle = () => {
    const finalTableData = autoCalculate(tableData);
    updateTableData(tableName, finalTableData);
    setTableData(finalTableData);
    setUnsavedChanges(false);
    setSaveDailogOpen(false);
    setSnackbarOpen(true);
  };

  const handleBackButton = () => {
    if (unsavedChanges) {
      setConfirmBack(true);
    } else {
      handleBack();
    }
  };

  const confirmBackHandle = () => {
    const finalTableData = autoCalculate(tableData);
    updateTableData(tableName, finalTableData);
    setUnsavedChanges(false);
    handleBack();
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  // --- FORMATTING HELPERS ---
  const formatRupee = (value, isManualInput = false) => {
    if (isManualInput && (!value || value === 0 || value === '0' || value === '0.00')) return '';
    if (!value && value !== 0) return '';
    const num = safeParseFloat(value);
    
    const isNegative = num < 0;
    const absValue = Math.abs(num);

    const hasDecimal = String(absValue).includes('.');
    const minDecimals = hasDecimal || !isManualInput ? 2 : 0;

    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: minDecimals,
      maximumFractionDigits: 2,
    });
    const formatted = formatter.format(absValue).replace('INR', '₹');
    
    return isNegative ? `-${formatted}` : formatted;
  };

  const parseRupee = (value) => {
    if (!value) return '';
    let cleaned = value.replace(/[^0-9.-]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
    
    if (cleaned.startsWith('-')) {
      cleaned = '-' + cleaned.substring(1).replace(/-/g, '');
    } else {
      cleaned = cleaned.replace(/-/g, '');
    }
    return cleaned;
  };

  // --- INPUT HANDLER ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const [itemName, field, type] = name.split('.');
    const cleanedValue = parseRupee(value);

    setTableData(prev => {
      const updated = prev.map(item =>
        item.name === itemName && (item.type === type || (item.type === null && type === ''))
          ? { ...item, [field]: cleanedValue }
          : item
      );
      return autoCalculate(updated);
    });

    setUnsavedChanges(true);
  };

  // --- RENDER ROWS ---
  const renderTableRows = () => {
    const getRowColor = (index) => ['#e0e7ff', '#e9d5ff', '#f3e8ff', '#ede9fe', '#ddd6fe'][index % 5];

    return tableData.map((item, index) => {
      const isSubTotalRow = isCalculatedRow(item.name);
      
      // Check for E or F rows (indices 12-14)
      const isEOrFRow = (index === rowIndices.B_start + 9 && item.type === 'Inputs') ||
                        (index === rowIndices.B_start + 10 && item.type === 'Capital Goods') ||
                        (index === rowIndices.B_start + 11);

      const isKOrLRow = index === rowIndices.K || index === rowIndices.L;

      return (
        <div key={index} style={{
          display: 'grid',
          gridTemplateColumns: '60px 1fr 100px 150px 150px 150px 150px',
          gap: '10px',
          alignItems: 'center',
          padding: '8px 10px',
          borderBottom: '1px solid #f1f5f9',
          backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
          borderRadius: '6px',
          marginBottom: '2px',
          minHeight: '50px'
        }}>
          <span style={{
            color: '#1e293b', fontSize: '13px', fontWeight: '600', textAlign: 'center',
            padding: '8px 6px', backgroundColor: getRowColor(index), borderRadius: '6px'
          }}>
            {index + 1}
          </span>

          <span style={{
            color: '#334155', fontSize: '13px', padding: '8px 12px',
            backgroundColor: '#f1f5f9', borderRadius: '6px', wordBreak: 'break-word'
          }}>
            {item.name}
          </span>

          <span style={{
            color: '#334155', fontSize: '13px', padding: '8px 12px',
            backgroundColor: '#f1f5f9', borderRadius: '6px', textAlign: 'center'
          }}>
            {item.type || ''}
          </span>

          {['igst', 'cgst', 'sgst', 'cess'].map((field) => {
            const shouldHideValue = (isEOrFRow && (field === 'cgst' || field === 'sgst')) ||
                                    (isKOrLRow && (field === 'igst' || field === 'cess'));

            const isFieldReadOnly = isSubTotalRow || !item.manual_type || shouldHideValue;

            let displayValue = '';
            
            if (shouldHideValue) {
                displayValue = '';
            } else if (isSubTotalRow) {
                displayValue = formatRupee(item[field]);
            } else {
                const raw = item[field] || '';
                const str = String(raw);
                displayValue = (raw === '' || raw === '-' || str.endsWith('.')) 
                    ? raw 
                    : formatRupee(safeParseFloat(raw), true); 
            }

            return (
              <input
                key={`${item.name}.${field}.${item.type || ''}`}
                type="text"
                name={`${item.name}.${field}.${item.type || ''}`}
                value={displayValue}
                onChange={handleInputChange}
                readOnly={isFieldReadOnly}
                style={{
                  color: '#1e293b', fontWeight: '500', fontSize: '13px', textAlign: 'right',
                  padding: '8px 12px', 
                  backgroundColor: shouldHideValue ? '#bbb8b8ff' : (isFieldReadOnly ? '#f0f0f0' : '#ecfdf5'),
                  border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none',
                  cursor: isFieldReadOnly ? 'not-allowed' : 'text',
                  ...(isSubTotalRow ? { fontWeight: 'bold', borderColor: '#34d399', borderWidth: '2px' } : {})
                }}
                onFocus={(e) => {
                  if (!isFieldReadOnly) {
                    e.target.value = item[field] || ''; 
                    e.target.style.borderColor = '#88d8a3';
                    e.target.style.boxShadow = '0 0 0 2px rgba(136, 216, 163, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  if (!isFieldReadOnly) {
                    e.target.value = formatRupee(safeParseFloat(item[field] || ''), true);
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              />
            );
          })}
        </div>
      );
    });
  };

  // --- JSX RENDER ---
  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ backgroundColor: 'white', borderRadius: '20px', padding: '30px', boxShadow: '0 20px 40px rgba(0,0,0,0.1)' }}>
        <BoxWrapper backButtonForZohoEnable={true} backButton={handleBackButton} boxTitle={tableName}>
          <p style={{ marginLeft: '10px' }}>
            <span style={{ fontWeight: 'bold' }}>Note: </span> The fields where the system computed values would be modified by more/less 20% shall be highlighted in <span style={{ color: 'red' }}>Red</span> for reference and attention.
          </p>
          <hr style={{ marginLeft: '10px' }} />
        </BoxWrapper>

        <div style={{ maxHeight: 'calc(100vh - 450px)', overflowY: 'auto', marginTop: '10px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 100px 150px 150px 150px 150px',
            gap: '10px',
            padding: '8px 10px',
            borderBottom: '2px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            borderRadius: '6px',
            fontWeight: '600',
            color: '#475569',
            position: 'sticky',
            top: 0,
            zIndex: '10'
          }}>
            <span style={{ textAlign: 'center' }}>S.No.</span>
            <span style={{ textAlign: 'left' }}>Details</span>
            <span style={{ textAlign: 'center' }}>Type</span>
            <span style={{ textAlign: 'right' }}>Integrated Tax (₹)</span>
            <span style={{ textAlign: 'right' }}>Central Tax (₹)</span>
            <span style={{ textAlign: 'right' }}>State/UT Tax (₹)</span>
            <span style={{ textAlign: 'right' }}>CESS (₹)</span>
          </div>

          {tableData && tableData.length > 0 ? renderTableRows() : (
            <div style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>
              No Data Found!
            </div>
          )}
        </div>

        <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '8px', borderTop: '2px solid #e2e8f0' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#64748b', textAlign: 'center' }}>
            Total Records: {tableData?.length || 0}
          </p>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', gap: '10px' }}>
          <button
            onClick={() => setSaveDailogOpen(true)}
            style={{
              padding: '10px 20px',
              background: 'linear-gradient(135deg, #ffd3a5 0%, #fd9853 50%, #e8a87c 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(255,211,165,0.3)'
            }}
          >
            Save
          </button>
        </div>
      </div>

      <ConfirmationDialog open={saveDailog} onClose={() => setSaveDailogOpen(false)} onConfirm={submitHandle} title="Confirm Save" text="Do you want to save your changes?" />
      <ConfirmationDialog open={confirmBack} onConfirm={confirmBackHandle} onClose={() => setConfirmBack(false)} title="Unsaved Changes" content="You have unsaved changes. Save before leaving?" />

      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleSnackbarClose} severity="success">Data saved successfully!</Alert>
      </Snackbar>
    </div>
  );
}

export default Six;